import type { FeatureLinkTarget } from "~/hec/components/molecules/feature-link/types";
import { getDetermineRouteTypeAndUrl } from "~/hec/hooks/useDetermineRouteTypeAndUrl";
import type { SiteId } from "~/hec/utils/site-id";

import { buttonFunctions } from "../cta/cta.helper";
import { routeTypes } from "../cta/utils";
import type { BuilderFeatureLinkInputs } from "./types";

// The external URL is `externalLinkReference.value.data.url`, inlined by the
// page loader (COM-198 on `routes/$.tsx`; PLP and search use
// `resolveAndInlineReferences`). A reference the loader did not resolve has no
// `data`, so the card renders unlinked instead of suspending on a fetch.
const externalUrl = (
  reference: BuilderFeatureLinkInputs["externalLinkReference"],
) => {
  const data = reference?.value?.data as { url?: unknown } | undefined;
  return typeof data?.url === "string" ? { url: data.url } : undefined;
};

/**
 * Builder link inputs → where the link goes. The toggle decides which input
 * counts, so a Custom URL left behind after switching to an External Link is
 * ignored. The External Link is read from the reference the page loader
 * inlines (COM-198); an unresolved one gives no URL and the card renders
 * unlinked rather than suspending.
 */
export const toFeatureLinkTarget = (
  inputs: BuilderFeatureLinkInputs,
  siteId: SiteId,
): FeatureLinkTarget => {
  const clickFunction = inputs.clickFunction ?? buttonFunctions.linkToPage;
  const common = {
    clickFunction,
    openInNewTab: inputs.openInNewTab,
    isRevShare: inputs.isRevShare,
  };

  if (clickFunction === buttonFunctions.linkToElement) {
    return {
      ...common,
      routeType: routeTypes.ELEMENT,
      scrollTargetId: inputs.scrollTargetId,
    };
  }
  if (clickFunction !== buttonFunctions.linkToPage) return common;

  const isInternalLink = inputs.isInternalLink !== false;
  const { routeType, url } = getDetermineRouteTypeAndUrl({
    clickFunction,
    isInternalLink,
    customUrl: isInternalLink ? inputs.customUrl : undefined,
    externalLinkReference: isInternalLink
      ? undefined
      : externalUrl(inputs.externalLinkReference),
    siteId,
  });
  return {
    ...common,
    routeType: routeType ?? undefined,
    url: url ?? undefined,
  };
};
